<template>
  <section class="dp-layout fe-edu-page">
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="handleTreeNodeClick" />
    </section>

    <section class="dp-right fe-main">
      <header class="fe-page-header">
          <div class="fe-page-header__left">
            <div class="fe-page-header__icon">
              <i class="el-icon-s-data"></i>
            </div>
            <div class="fe-page-header__text">
              <h2 class="fe-page-header__title">班级评分情况</h2>
              <p class="fe-page-header__sub">{{ scopeLabel || "请在左侧选择学校" }}</p>
            </div>
          </div>
          <div class="fe-page-header__actions">
            <el-button
              type="primary"
              size="small"
              icon="el-icon-download"
              class="fe-btn-primary"
              :loading="exporting"
              :disabled="!schoolDepartmentId || !tableData.length"
              @click="exportListTable"
            >导出数据</el-button>
          </div>
        </header>

        <div v-if="!schoolDepartmentId" class="fe-empty-state">
          <div class="fe-empty-state__icon">
            <i class="el-icon-school"></i>
          </div>
          <h3 class="fe-empty-state__title">选择学校查看评分</h3>
          <p class="fe-empty-state__desc">在左侧部门树中选择学校，查看各班级评分进度</p>
        </div>

        <template v-else>
          <div class="toolbar-filter-bar fe-filter-bar fe-filter-bar--single">
            <el-form :inline="true" class="toolbar-filter-form">
              <div class="toolbar-filter-fields fe-filter-bar__row">
                <FePeriodFilter
                  :school-department-id="schoolDepartmentId"
                  :period-type.sync="periodType"
                  :period-key.sync="periodKey"
                  @change="handlePeriodQuery"
                />
                <el-form-item label="状态">
                  <el-select
                    v-model="statusFilter"
                    placeholder="全部状态"
                    clearable
                    class="toolbar-field-w-md"
                  >
                    <el-option label="未开启" value="idle" />
                    <el-option label="进行中" value="active" />
                    <el-option label="已结束" value="done" />
                  </el-select>
                </el-form-item>
                <el-form-item label="发起时间">
                  <el-date-picker
                    v-model="dateRange"
                    type="daterange"
                    value-format="yyyy-MM-dd"
                    start-placeholder="开始日期"
                    end-placeholder="结束日期"
                    class="toolbar-field-w-daterange"
                    clearable
                  />
                </el-form-item>
                <div class="fe-filter-bar__actions">
                  <el-button type="primary" size="small" icon="el-icon-search" @click="handleQuery">查询</el-button>
                  <el-button size="small" type="info" icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
                </div>
              </div>
            </el-form>
          </div>

          <div class="fe-info-tip">
            <i class="el-icon-info"></i>
            <span>左侧部门树限定班级范围；可按状态、发起时间筛选，并查看各班级教师参与情况</span>
          </div>

          <section class="fe-card fe-card--fill">
            <div class="fe-card__body">
              <div class="tbl-scroll-host">
                <el-table
                  v-loading="loading"
                  :data="pagedTableData"
                  height="100%"
                  class="fe-table fe-table--center"
                  style="width: 100%"
                  empty-text="暂无班级评分数据"
                >
            <el-table-column label="序号" :resizable="false" width="60" align="center" header-align="center">
              <template slot-scope="{ $index }">
                {{ (pageIndex - 1) * pageSize + $index + 1 }}
              </template>
            </el-table-column>
            <el-table-column label="班级" prop="ClassName" min-width="120" align="center" header-align="center" />
            <el-table-column label="评价周期" min-width="120" align="center" header-align="center">
              <template slot-scope="{ row }">{{ row.PeriodLabel || "" }}</template>
            </el-table-column>
            <el-table-column label="最近评分发起时间" min-width="160" align="center" header-align="center">
              <template slot-scope="{ row }">{{ row.OpenedAt | dateFormat }}</template>
            </el-table-column>
            <el-table-column label="结束时间" min-width="160" align="center" header-align="center">
              <template slot-scope="{ row }">{{ row.ClosedAt | dateFormat }}</template>
            </el-table-column>
            <el-table-column label="状态" width="90" align="center" header-align="center">
              <template slot-scope="{ row }">
                <span :class="getStatusClass(row)">{{ getStatusText(row) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="班主任" width="90" align="center" header-align="center">
              <template slot-scope="{ row }">
                <template v-if="row.LatestRoundNo > 0">
                  <el-button
                    type="text"
                    :class="row.HomeroomSideComplete ? 'fe-link-action' : 'fe-link-warn'"
                    @click="openTeacherDialog(row, row.HomeroomSideComplete ? 'completed' : 'unfinished', 'homeroom')"
                  >{{ row.HomeroomSideComplete ? "已完成" : "未完成" }}</el-button>
                </template>
              </template>
            </el-table-column>
            <el-table-column label="参与教师" :resizable="false" width="90" align="center" header-align="center">
              <template slot-scope="{ row }">
                <el-button
                  v-if="row.LatestRoundNo > 0"
                  type="text"
                  class="fe-link-action"
                  @click="openTeacherDialog(row, 'completed', 'subject')"
                >{{ row.CompletedTeacherCount }}</el-button>
              </template>
            </el-table-column>
            <el-table-column label="未完成教师" width="100" align="center" header-align="center">
              <template slot-scope="{ row }">
                <el-button
                  v-if="row.LatestRoundNo > 0 && getUnfinishedCount(row) > 0"
                  type="text"
                  class="fe-link-warn"
                  @click="openTeacherDialog(row, 'unfinished', 'subject')"
                >{{ getUnfinishedCount(row) }}</el-button>
                <span v-else-if="row.LatestRoundNo > 0">0</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" :resizable="false" width="140" align="center" header-align="center">
              <template slot-scope="{ row }">
                <el-button
                  v-if="row.LatestRoundNo > 0"
                  type="text"
                  class="fe-link-action"
                  @click="openHistory(row)"
                >历史评分</el-button>
                <el-button
                  v-if="row.LatestRoundNo > 0"
                  type="text"
                  class="fe-link-action"
                  @click="exportRowTable(row)"
                >导出</el-button>
              </template>
            </el-table-column>
                </el-table>
              </div>
            </div>
            <div class="fe-card__foot">
              <el-pagination
                layout="total, prev, pager, next, jumper"
                :current-page.sync="pageIndex"
                :page-size="pageSize"
                :total="tableData.length"
                @current-change="handlePageChange"
              />
            </div>
          </section>
        </template>
    </section>

    <el-dialog
      :title="'历史评分 — ' + historyClassName"
      :visible.sync="historyDialogVisible"
      width="560px"
      top="8vh"
      custom-class="fe-dialog fe-history-dialog"
      append-to-body
      @closed="handleHistoryDialogClosed"
    >
      <div v-loading="historyLoading" class="fe-history-dialog__body">
        <div v-if="historyList.length" class="fe-history-stats">
          <div class="fe-history-stats__icon">
            <i class="el-icon-time"></i>
          </div>
          <div class="fe-history-stats__content">
            <div class="fe-history-stats__head">
              <div class="fe-history-stats__primary">
                <span class="fe-history-stats__num">{{ historyList.length }}</span>
                <span class="fe-history-stats__unit">次</span>
                <span class="fe-history-stats__label">累计评分</span>
              </div>
              <el-button
                type="primary"
                size="small"
                icon="el-icon-download"
                class="fe-btn-primary fe-history-stats__export"
                :loading="exporting"
                @click="exportHistoryTable"
              >导出全部</el-button>
            </div>
            <div v-if="historyStats.finished || historyStats.active" class="fe-history-stats__tags">
              <span v-if="historyStats.finished" class="fe-history-stats__tag fe-history-stats__tag--done">
                已结束 {{ historyStats.finished }}
              </span>
              <span v-if="historyStats.active" class="fe-history-stats__tag fe-history-stats__tag--active">
                进行中 {{ historyStats.active }}
              </span>
            </div>
          </div>
        </div>
        <div v-if="!historyLoading && !historyList.length" class="fe-history-empty">
          暂无历史评分记录
        </div>
        <div v-else class="fe-history-list">
          <div
            v-for="(row, index) in pagedHistoryData"
            :key="row.RoundNo + '-' + (row.PeriodKey || index)"
            class="fe-history-item"
          >
            <div class="fe-history-item__top">
              <div class="fe-history-item__title">
                <span class="fe-history-item__index">
                  {{ (historyPageIndex - 1) * historyPageSize + index + 1 }}
                </span>
                <span class="fe-history-item__period">
                  {{ row.PeriodLabel || ("第" + row.RoundNo + "轮") }}
                </span>
                <span :class="getHistoryStatusClass(row)">{{ getHistoryStatusText(row) }}</span>
              </div>
              <el-button
                type="text"
                class="fe-link-action fe-history-item__export"
                @click="exportHistoryRow(row)"
              >导出</el-button>
            </div>
            <div class="fe-history-item__time">
              {{ formatHistoryDate(row.OpenedAt) || "—" }}
              <span class="fe-history-time-sep">至</span>
              {{ row.ClosedAt ? formatHistoryDate(row.ClosedAt) : "—" }}
            </div>
            <div class="fe-history-item__progress">
              <button
                type="button"
                class="fe-history-chip"
                :class="row.HomeroomSideComplete ? 'is-done' : 'is-warn'"
                @click="openHistoryTeacherDialog(row, row.HomeroomSideComplete ? 'completed' : 'unfinished', 'homeroom')"
              >
                班主任 · {{ row.HomeroomSideComplete ? "已完成" : "未完成" }}
              </button>
              <button
                type="button"
                class="fe-history-chip is-done"
                @click="openHistoryTeacherDialog(row, 'completed', 'subject')"
              >
                任教已完成 {{ row.CompletedTeacherCount || 0 }}
              </button>
              <button
                v-if="row.UnfinishedTeacherCount > 0"
                type="button"
                class="fe-history-chip is-warn"
                @click="openHistoryTeacherDialog(row, 'unfinished', 'subject')"
              >
                任教未完成 {{ row.UnfinishedTeacherCount }}
              </button>
              <span v-else class="fe-history-chip is-muted">任教未完成 0</span>
            </div>
          </div>
        </div>
        <div v-if="historyList.length" class="fe-dialog-pager">
          <el-pagination
            layout="total, prev, pager, next"
            :current-page.sync="historyPageIndex"
            :page-size="historyPageSize"
            :total="historyList.length"
          />
        </div>
      </div>
      <div slot="footer" class="fe-history-dialog__footer">
        <el-button size="small" @click="historyDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="teacherDialogTitle"
      :visible.sync="teacherDialogVisible"
      width="560px"
      custom-class="fe-dialog fe-teacher-dialog"
      append-to-body
      @closed="handleTeacherDialogClosed"
    >
      <div v-loading="teacherDialogLoading" class="fe-teacher-dialog__body">
        <el-tabs v-model="teacherDialogRoleTab" @tab-click="applyTeacherDialogFilter">
          <el-tab-pane label="班主任" name="homeroom" />
          <el-tab-pane label="任教" name="subject" />
        </el-tabs>
        <el-table
          :data="pagedTeacherDialogData"
          class="fe-table fe-table--center"
          size="small"
          height="248"
          style="width: 100%"
          empty-text="暂无提交数据"
        >
          <el-table-column label="教师" prop="TeacherName" min-width="90" align="center" header-align="center" />
          <el-table-column label="科目" prop="SubjectName" min-width="90" align="center" header-align="center" />
          <el-table-column label="状态" width="80" align="center" header-align="center">
            <template slot-scope="{ row }">
              <el-tag
                :type="row.IsVoided ? 'info' : row.IsComplete ? 'success' : 'warning'"
                size="mini"
              >
                {{ row.IsVoided ? "已作废" : row.IsComplete ? "已提交" : "未提交" }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="最近提交时间" prop="SubmittedAt" min-width="150" align="center" header-align="center">
            <template slot-scope="{ row }">{{ row.SubmittedAt | dateFormat }}</template>
          </el-table-column>
        </el-table>
      </div>
      <div slot="footer" class="fe-teacher-dialog__footer">
        <el-pagination
          layout="total, prev, pager, next, jumper"
          :current-page.sync="teacherDialogPageIndex"
          :page-size="teacherDialogPageSize"
          :total="teacherDialogList.length"
        />
      </div>
    </el-dialog>
  </section>
</template>

<script>
import DepartmentPane from "@/components/DepartmentPane";
import {
  getClassRatingStatusList,
  getClassRoundHistory,
  getTeacherSubmitDetail,
  exportClassRatingStatusList,
  exportClassRoundHistory,
  exportClassRoundDetail,
  downloadBlobExcel,
  getCurrentMonthPeriodKey,
  collectClassIdsFromTreeNode,
  isClassDeptNode,
  isSchoolDeptNode,
  resolveSchoolIdFromTreeNode,
} from "@/api/fiveEducation";
import { normalizeDeptId, treeNodeDeptId } from "@/utils/deptCascaderProps";
import FePeriodFilter from "./FePeriodFilter.vue";
import "./fe-theme.css";
import "./fe-theme-global.css";

export default {
  name: "ClassRatingStatus",
  components: { DepartmentPane, FePeriodFilter },
  data() {
    return {
      schoolDepartmentId: null,
      scopeLabel: "",
      filterClassIds: null,
      periodType: "month",
      periodKey: getCurrentMonthPeriodKey(),
      loading: false,
      allTableData: [],
      tableData: [],
      dateRange: null,
      statusFilter: "",
      pageIndex: 1,
      pageSize: 10,

      historyDialogVisible: false,
      historyClassId: null,
      historyClassName: "",
      historyLoading: false,
      historyList: [],
      historyPageIndex: 1,
      historyPageSize: 5,

      teacherDialogVisible: false,
      teacherDialogTitle: "",
      teacherDialogList: [],
      teacherDialogRawList: [],
      teacherDialogType: "completed",
      teacherDialogRoleTab: "subject",
      teacherDialogPageIndex: 1,
      teacherDialogPageSize: 5,
      teacherDialogLoading: false,
      exporting: false,
    };
  },
  computed: {
    pagedTableData() {
      const start = (this.pageIndex - 1) * this.pageSize;
      return this.tableData.slice(start, start + this.pageSize);
    },
    pagedHistoryData() {
      const start = (this.historyPageIndex - 1) * this.historyPageSize;
      return this.historyList.slice(start, start + this.historyPageSize);
    },
    /** 历史弹窗统计：已结束 / 进行中轮次数 */
    historyStats() {
      let finished = 0;
      let active = 0;
      for (let i = 0; i < this.historyList.length; i++) {
        if (this.historyList[i].IsOpen) active += 1;
        else finished += 1;
      }
      return { finished: finished, active: active };
    },
    /** 教师明细弹窗分页数据 */
    pagedTeacherDialogData() {
      const start = (this.teacherDialogPageIndex - 1) * this.teacherDialogPageSize;
      return this.teacherDialogList.slice(start, start + this.teacherDialogPageSize);
    },
  },
  methods: {
    /** 部门树点击：学校显示全校班级，班级/年级显示范围内班级 */
    handleTreeNodeClick(data, node) {
      if (!data || !node) return;

      this.schoolDepartmentId = resolveSchoolIdFromTreeNode(node, data.value);
      if (!this.schoolDepartmentId) {
        this.scopeLabel = "";
        this.filterClassIds = null;
        this.allTableData = [];
        this.tableData = [];
        this.dateRange = null;
        return;
      }

      if (isClassDeptNode(node)) {
        this.scopeLabel = data.label || "";
        this.filterClassIds = [treeNodeDeptId(data)];
      } else if (isSchoolDeptNode(node)) {
        this.scopeLabel = data.label || "";
        this.filterClassIds = null;
      } else {
        this.scopeLabel = data.label || "";
        const classIds = collectClassIdsFromTreeNode(node);
        this.filterClassIds = classIds.length ? classIds : null;
      }

      // 切换部门只改变范围，不重置筛选条件（日期、状态、周期由用户自行控制）
      this.pageIndex = 1;
      this.loadList();
    },

    /** 周期筛选查询 */
    handlePeriodQuery() {
      this.pageIndex = 1;
      this.loadList();
    },

    /** 加载班级评分情况列表 */
    async loadList() {
      if (!this.schoolDepartmentId) return;
      this.loading = true;
      try {
        const res = await getClassRatingStatusList(
          this.schoolDepartmentId,
          this.periodType,
          this.periodKey
        );
        if (res.data && res.data.success) {
          this.allTableData = res.data.response || [];
          this.applyFilter();
        }
      } finally {
        this.loading = false;
      }
    },

    /** 按部门树范围 + 发起时间过滤 */
    applyFilter() {
      let list = this.allTableData.slice();

      if (this.filterClassIds && this.filterClassIds.length) {
        const idSet = {};
        this.filterClassIds.forEach(function (id) {
          idSet[String(normalizeDeptId(id))] = true;
        });
        list = list.filter(function (row) {
          return idSet[String(normalizeDeptId(row.ClassDepartmentId))];
        });
      }

      if (this.dateRange && this.dateRange.length === 2) {
        const startMs = new Date(this.dateRange[0] + " 00:00:00").getTime();
        const endMs = new Date(this.dateRange[1] + " 23:59:59").getTime();
        list = list.filter(function (row) {
          if (!row.OpenedAt) return false;
          const openedMs = new Date(row.OpenedAt).getTime();
          return openedMs >= startMs && openedMs <= endMs;
        });
      }

      if (this.statusFilter) {
        const statusKey = this.statusFilter;
        const self = this;
        list = list.filter(function (row) {
          return self.getStatusKey(row) === statusKey;
        });
      }

      this.tableData = list;
      this.pageIndex = 1;
    },

    async handleQuery() {
      // 查询按钮统一刷新接口，确保当前周期数据和班主任/任教进度是最新的
      await this.loadList();
    },

    handleReset() {
      this.dateRange = null;
      this.statusFilter = "";
      this.periodType = "month";
      this.periodKey = getCurrentMonthPeriodKey();
      this.loadList();
    },

    handlePageChange(page) {
      this.pageIndex = page;
    },

    getUnfinishedCount(row) {
      if (row.UnfinishedTeacherCount != null) return row.UnfinishedTeacherCount;
      return Math.max(0, (row.ExpectedTeacherCount || 0) - (row.CompletedTeacherCount || 0));
    },

    getStatusKey(row) {
      if (row.IsOpen) return "active";
      if (row.LatestRoundNo > 0) return "done";
      return "idle";
    },

    getStatusText(row) {
      if (row.IsOpen) return "进行中";
      if (row.LatestRoundNo > 0) return "已结束";
      return "未开启";
    },

    getStatusClass(row) {
      if (row.IsOpen) return "fe-status fe-status--active";
      if (row.LatestRoundNo > 0) return "fe-status fe-status--done";
      return "fe-status fe-status--idle";
    },

    getHistoryStatusText(row) {
      if (row.IsVoided) return "已作废";
      return row.IsOpen ? "进行中" : "已结束";
    },

    getHistoryStatusClass(row) {
      if (row.IsVoided) return "fe-status fe-status--idle";
      return row.IsOpen ? "fe-status fe-status--active" : "fe-status fe-status--done";
    },

    /** 历史评分时间展示 */
    formatHistoryDate(val) {
      if (!val) return "";
      return new Date(val).toLocaleString("zh-CN", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    },

    /** 打开历史评分弹窗 */
    async openHistory(row) {
      this.historyClassId = row.ClassDepartmentId;
      this.historyClassName = row.ClassName || "";
      this.historyPageIndex = 1;
      this.historyList = [];
      this.historyDialogVisible = true;
      this.historyLoading = true;
      try {
        const res = await getClassRoundHistory(row.ClassDepartmentId);
        if (res.data && res.data.success) {
          this.historyList = res.data.response || [];
        } else {
          this.historyList = [];
          this.$message.error((res.data && res.data.msg) || "加载历史记录失败");
        }
      } catch (e) {
        this.historyList = [];
        this.$message.error("网络错误");
      } finally {
        this.historyLoading = false;
      }
    },

    handleHistoryDialogClosed() {
      this.historyList = [];
      this.historyPageIndex = 1;
      this.historyClassId = null;
      this.historyClassName = "";
    },

    /** 打开教师参与明细；roleTab=homeroom|subject */
    async openTeacherDialog(row, type, roleTab) {
      await this.loadTeacherDialog(
        row.ClassName,
        row.LatestRoundNo,
        row.ClassDepartmentId,
        type,
        row.PeriodLabel,
        roleTab || "subject"
      );
    },

    async openHistoryTeacherDialog(row, type, roleTab) {
      await this.loadTeacherDialog(
        this.historyClassName,
        row.RoundNo,
        this.historyClassId,
        type,
        row.PeriodLabel,
        roleTab || "subject"
      );
    },

    async loadTeacherDialog(className, roundNo, classId, type, rowPeriodLabel, roleTab) {
      const role = roleTab === "homeroom" ? "homeroom" : "subject";
      const typeLabel = type === "completed"
        ? (role === "homeroom" ? "班主任已完成" : "参与教师")
        : (role === "homeroom" ? "班主任未完成" : "未完成教师");
      this.teacherDialogTitle = (className || "") + " " + (rowPeriodLabel || ("第" + roundNo + "轮")) + " — " + typeLabel;
      this.teacherDialogVisible = true;
      this.teacherDialogPageIndex = 1;
      this.teacherDialogLoading = true;
      this.teacherDialogType = type;
      this.teacherDialogRoleTab = role;
      this.teacherDialogRawList = [];
      this.teacherDialogList = [];
      try {
        const res = await getTeacherSubmitDetail(classId, roundNo);
        if (res.data && res.data.success) {
          this.teacherDialogRawList = res.data.response || [];
          this.applyTeacherDialogFilter();
        }
      } finally {
        this.teacherDialogLoading = false;
      }
    },

    /** 按角色页签 + 完成态过滤明细 */
    applyTeacherDialogFilter() {
      const type = this.teacherDialogType;
      const role = this.teacherDialogRoleTab || "subject";
      this.teacherDialogPageIndex = 1;
      this.teacherDialogList = (this.teacherDialogRawList || []).filter(function (item) {
        if (item.IsVoided) return false;
        const itemRole = item.OperatorRole || "subject";
        if (itemRole !== role) return false;
        return type === "completed" ? item.IsComplete : !item.IsComplete;
      });
    },

    handleTeacherDialogClosed() {
      this.teacherDialogList = [];
      this.teacherDialogRawList = [];
      this.teacherDialogPageIndex = 1;
      this.teacherDialogRoleTab = "subject";
      this.teacherDialogType = "completed";
    },

    /** 构建当前列表班级 Id（导出用，与表格展示一致） */
    buildExportClassIds() {
      if (!this.tableData.length) return "";
      const ids = [];
      const seen = {};
      for (let i = 0; i < this.tableData.length; i++) {
        const id = normalizeDeptId(this.tableData[i].ClassDepartmentId);
        if (id && !seen[id]) {
          seen[id] = true;
          ids.push(id);
        }
      }
      return ids.join(",");
    },

    /** 处理导出接口错误（blob 响应失败时后端可能返回 JSON） */
    handleExportError(err) {
      const self = this;
      const data = err && err.response && err.response.data;
      if (data && typeof data.text === "function") {
        data.text().then(function (text) {
          try {
            const json = JSON.parse(text);
            self.$message.error(json.msg || json.message || json.title || "导出失败");
          } catch (e) {
            self.$message.error("导出失败");
          }
        });
        return;
      }
      if (data && typeof FileReader !== "undefined") {
        const reader = new FileReader();
        reader.onload = function () {
          try {
            const json = JSON.parse(reader.result);
            self.$message.error(json.msg || json.message || "导出失败");
          } catch (e) {
            self.$message.error("导出失败");
          }
        };
        reader.readAsText(data);
        return;
      }
      this.$message.error("导出失败");
    },

    /** 导出当前列表 */
    async exportListTable() {
      if (!this.schoolDepartmentId) return;
      this.exporting = true;
      try {
        const res = await exportClassRatingStatusList(
          this.schoolDepartmentId,
          this.buildExportClassIds(),
          this.periodType,
          this.periodKey
        );
        downloadBlobExcel(res, "班级评分情况");
      } catch (e) {
        this.handleExportError(e);
      } finally {
        this.exporting = false;
      }
    },

    /** 导出单行最新轮次 */
    async exportRowTable(row) {
      this.exporting = true;
      try {
        const res = await exportClassRoundDetail(row.ClassDepartmentId, row.LatestRoundNo);
        downloadBlobExcel(res, (row.ClassName || "班级") + "_最新评分");
      } catch (e) {
        this.handleExportError(e);
      } finally {
        this.exporting = false;
      }
    },

    /** 导出历史列表 */
    async exportHistoryTable() {
      if (!this.historyClassId) return;
      this.exporting = true;
      try {
        const res = await exportClassRoundHistory(this.historyClassId);
        downloadBlobExcel(res, this.historyClassName + "_历史评分");
      } catch (e) {
        this.handleExportError(e);
      } finally {
        this.exporting = false;
      }
    },

    /** 导出历史单行 */
    async exportHistoryRow(row) {
      this.exporting = true;
      try {
        const res = await exportClassRoundDetail(this.historyClassId, row.RoundNo);
        downloadBlobExcel(res, this.historyClassName + "_第" + row.RoundNo + "轮");
      } catch (e) {
        this.handleExportError(e);
      } finally {
        this.exporting = false;
      }
    },
  },
  filters: {
    dateFormat(val) {
      if (!val) return "";
      return new Date(val).toLocaleString("zh-CN", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
/* 本页纵向间距略收紧。最后更新：2026-09-01 */
.fe-main {
  gap: 8px;
}
.fe-page-header__left {
  flex: 1;
  min-width: 0;
  flex-wrap: nowrap;
}
.fe-page-header__actions {
  flex-wrap: nowrap;
  margin-left: 16px;
  flex-shrink: 0;
}
/* 筛选收成一行：周期 + 状态 + 时间 + 操作 */
.fe-filter-bar--single.fe-filter-bar >>> .toolbar-filter-form {
  flex-direction: row !important;
  align-items: center !important;
  flex-wrap: wrap;
  gap: 8px 12px;
}
.fe-filter-bar--single .fe-filter-bar__row {
  flex-wrap: wrap;
  width: 100%;
}
.fe-filter-bar--single .fe-filter-bar__actions {
  margin-left: auto;
}
.fe-filter-bar--single.fe-filter-bar {
  padding: 10px 14px;
  margin-bottom: 0; /* 去掉 toolbar-filter-bar 默认底边距，间距交给 .fe-main gap */
}
.fe-info-tip {
  padding: 8px 14px;
}
</style>
